import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FaFutbol, FaFootballBall, FaCheck, FaTimes, FaFilter, FaChevronLeft, FaChevronRight, FaFire, FaCalendarAlt } from 'react-icons/fa';
import { FaBasketball, FaVolleyball } from 'react-icons/fa6';
import { GiHockey } from 'react-icons/gi';
import { MdOutlineSportsHandball, MdSportsMma } from 'react-icons/md';
import { IoTennisball } from 'react-icons/io5';
import { SiCounterstrike } from 'react-icons/si';
import { CgMoreO } from 'react-icons/cg';
import { PiBoxingGlove, PiSoccerBallDuotone } from 'react-icons/pi';
import { sportColors } from '../../utils/sports';
import { useBetsData } from '../../utils/fetchBetsData';
import { getStreakFromHistory, getLossStreakFromHistory } from '../../utils/streak';
import { computeBetStats } from '../../utils/betStats';
import PageHeader from '../../components/PageHeader/PageHeader';
import BetsSummary from '../../components/BetsSummary/BetsSummary';
import BetsBreakdown from '../../components/BetsBreakdown/BetsBreakdown';
import { SkeletonBetList } from '../../components/Skeleton/Skeleton';
import { useLiveScores } from '../../hooks/useLiveScores';
import './BetsPage.css';

// Nomes dos meses em PT
const monthNames = {
    1: 'Janeiro', 2: 'Fevereiro', 3: 'Março', 4: 'Abril',
    5: 'Maio', 6: 'Junho', 7: 'Julho', 8: 'Agosto',
    9: 'Setembro', 10: 'Outubro', 11: 'Novembro', 12: 'Dezembro'
};

// Formata data para dd-mm-yyyy
const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    try {
        // Se já está no formato YYYY-MM-DD (ISO), converte diretamente sem usar Date()
        // para evitar problemas de timezone
        if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const [datePart] = dateStr.split('T'); // Remove tempo se existir
            const [year, month, day] = datePart.split('-');
            return `${day}-${month}-${year}`;
        }
        
        // Se está no formato DD/MM/YYYY
        if (typeof dateStr === 'string' && dateStr.includes('/')) {
            return dateStr.replace(/\//g, '-');
        }
        
        // Fallback: tenta criar um objeto Date
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            return `${day}-${month}-${year}`;
        }
        
        return String(dateStr);
    } catch (e) {
        return String(dateStr);
    }
};

// Formata hora para HH:MM (remove segundos)
const formatTime = (timeStr) => {
    if (!timeStr) return '';
    // Remove segundos se existirem (19:40:00 -> 19:40)
    const parts = timeStr.split(':');
    if (parts.length >= 2) {
        return `${parts[0]}:${parts[1]}`;
    }
    return timeStr;
};

// Teams line; shows the score between the teams when live data exists
function GameTeams({ game, live }) {
    const score = live?.score;
    const sets = live?.sets;
    if (!game.awayTeam) {
        return <span className="bets-game-teams">{game.homeTeam}</span>;
    }
    if (score && live.state !== 'pre') {
        return (
            <span className="bets-game-teams">
                {game.homeTeam}
                <RedCards count={live.reds?.home?.length} />
                <span className={`bets-game-score ${live.state === 'in' ? 'is-live' : ''}`}>{score.home}–{score.away}</span>
                {game.awayTeam}
                <RedCards count={live.reds?.away?.length} />
            </span>
        );
    }
    return (
        <span className="bets-game-teams">
            {game.homeTeam} vs {game.awayTeam}
            {sets && live.state !== 'pre' && (
                <span className={`bets-game-sets ${live.state === 'in' ? 'is-live' : ''}`}>
                    {sets.home.map((h, i) => `${h}-${sets.away[i] ?? 0}`).join(' ')}
                </span>
            )}
        </span>
    );
}

function RedCards({ count }) {
    if (!count) return null;
    return (
        <span className="red-cards" title={`${count} ${count === 1 ? 'cartão vermelho' : 'cartões vermelhos'}`}>
            {Array.from({ length: count }).map((_, i) => <span key={i} className="red-card" />)}
        </span>
    );
}

// "H. Kane 54' (P), 60'" — one entry per player, minutes in order
function groupByPlayer(events) {
    const byPlayer = new Map();
    events.forEach(ev => {
        const minute = `${ev.minute}${ev.penalty ? ' (P)' : ''}${ev.ownGoal ? ' (AG)' : ''}`;
        const entry = byPlayer.get(ev.player) || { player: ev.player, first: parseInt(ev.minute, 10), minutes: [] };
        entry.minutes.push(minute);
        byPlayer.set(ev.player, entry);
    });
    return [...byPlayer.values()].sort((a, b) => a.first - b.first);
}

// Compact scorer line per team (only teams with goals or red cards)
function GameIncidents({ game, live }) {
    if (!live || live.state === 'pre' || !live.goals) return null;
    const rows = [
        { key: 'home', team: game.homeTeam },
        { key: 'away', team: game.awayTeam },
    ].map(({ key, team }) => ({
        key,
        team,
        goals: groupByPlayer(live.goals[key] || []),
        reds: live.reds?.[key] || [],
    })).filter(r => r.goals.length || r.reds.length);
    if (!rows.length) return null;

    return (
        <div className="bets-game-incidents">
            {rows.map(row => (
                <p key={row.key} className="incidents-line">
                    <span className="incidents-team">{row.team}</span>
                    {row.goals.length > 0 && (
                        <span className="incidents-goals">
                            <FaFutbol className="incident-icon" aria-label="Golos" />
                            {row.goals.map(g => `${g.player} ${g.minutes.join(', ')}`).join(' · ')}
                        </span>
                    )}
                    {row.reds.map((r, i) => (
                        <span key={i} className="incidents-red">
                            <span className="red-card" aria-label="Cartão vermelho" />
                            {r.player} {r.minute}
                        </span>
                    ))}
                </p>
            ))}
        </div>
    );
}

// Kick-off time, or the live clock / final status
function GameWhen({ game, live }) {
    if (live?.state === 'in') {
        return (
            <span className="bets-game-datetime live-status is-live">
                <span className="live-dot" />
                Ao vivo{live.clock ? ` · ${live.clock}` : live.detail ? ` · ${live.detail}` : ''}
            </span>
        );
    }
    if (live?.state === 'post') {
        return <span className="bets-game-datetime live-status">Terminado · {formatDate(game.date)}</span>;
    }
    return <span className="bets-game-datetime">{formatDate(game.date)} · {formatTime(game.time)}</span>;
}

function BetsPage() {
    // Estado para seleção de mês
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [selectedYear, setSelectedYear] = useState(null);
    const [showMonthDropdown, setShowMonthDropdown] = useState(false);
    const monthDropdownRef = useRef(null);
    
    const { betsData, loading, error, getSports, getResults, availableMonths } = useBetsData(selectedMonth, selectedYear);
    const liveScores = useLiveScores();
    const [filters, setFilters] = useState({
        sport: 'all',
        result: 'all',
        betType: 'all',
    });
    const [showFilters, setShowFilters] = useState(false);
    const [showLegend, setShowLegend] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [isPageChanging, setIsPageChanging] = useState(false);
    const betsPerPage = 10;
    const betsListRef = useRef(null);

    // Fecha dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (monthDropdownRef.current && !monthDropdownRef.current.contains(event.target)) {
                setShowMonthDropdown(false);
            }
        };
        
        if (showMonthDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showMonthDropdown]);

    // Handler para seleção de mês
    const handleMonthSelect = (month, year) => {
        setSelectedMonth(month);
        setSelectedYear(year);
        setShowMonthDropdown(false);
        setCurrentPage(1);
    };

    // Handler para ver todos os meses
    const handleViewAll = () => {
        setSelectedMonth(null);
        setSelectedYear(null);
        setShowMonthDropdown(false);
        setCurrentPage(1);
    };

    // Label atual do seletor de mês
    const currentMonthLabel = selectedMonth && selectedYear
        ? `${monthNames[selectedMonth]} ${selectedYear}`
        : 'Todos os Meses';

    const sportIcons = {
        futebol: FaFutbol,
        basket: FaBasketball,
        voleibol: FaVolleyball,
        andebol: MdOutlineSportsHandball,
        hoquei: GiHockey,
        tenis: IoTennisball,
        futebolAmericano: FaFootballBall,
        mma: MdSportsMma,
        cs2: SiCounterstrike,
        boxe: PiBoxingGlove,
        futsal: PiSoccerBallDuotone,
        outros: CgMoreO,
    };

    const sportNames = {
        futebol: 'Futebol',
        basket: 'Basquetebol',
        voleibol: 'Voleibol',
        andebol: 'Andebol',
        hoquei: 'Hóquei',
        tenis: 'Ténis',
        futebolAmericano: 'Futebol Americano',
        mma: 'MMA/UFC',
        cs2: 'Counter Strike 2',
        boxe: 'Boxe',
        futsal: 'Futsal',
        outros: 'Outros',
    };

    const resultNames = {
        won: 'Ganho',
        lost: 'Perdido',
        pending: 'Pendente',
        void: 'Anulado',
        all: 'Todos'
    };

    const sports = getSports();
    const results = getResults();

    const filteredBets = useMemo(() => {
        return betsData.filter(bet => {
            if (filters.sport !== 'all') {
                const hasSport = bet.games.some(game => game.sport === filters.sport);
                if (!hasSport) return false;
            }
            if (filters.result !== 'all' && bet.result !== filters.result) {
                return false;
            }
            if (filters.betType !== 'all') {
                const isMultiple = bet.games.length > 1;
                if (filters.betType === 'simple' && isMultiple) return false;
                if (filters.betType === 'multiple' && !isMultiple) return false;
            }
            return true;
        });
    }, [betsData, filters]);

    const stats = useMemo(() => computeBetStats(filteredBets), [filteredBets]);

    const currentStreak = useMemo(() => {
        return getStreakFromHistory(betsData);
    }, [betsData]);

    const lossStreak = useMemo(() => {
        return getLossStreakFromHistory(betsData);
    }, [betsData]);

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value
        }));
        setCurrentPage(1);
    };

    const totalPages = Math.ceil(filteredBets.length / betsPerPage);
    const startIndex = (currentPage - 1) * betsPerPage;
    const endIndex = startIndex + betsPerPage;
    const currentBets = filteredBets.slice(startIndex, endIndex);

    const goToPage = (page) => {
        const newPage = Math.max(1, Math.min(page, totalPages));
        if (newPage !== currentPage) {
            setIsPageChanging(true);
            setTimeout(() => {
                setCurrentPage(newPage);
                setIsPageChanging(false);
                if (betsListRef.current) {
                    betsListRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 150);
        }
    };

    const clearFilters = () => {
        setFilters({ sport: 'all', result: 'all', betType: 'all' });
    };

    const hasActiveFilters = filters.sport !== 'all' || filters.result !== 'all' || filters.betType !== 'all';

    const activeFiltersCount = [
        filters.sport !== 'all',
        filters.result !== 'all',
        filters.betType !== 'all',
    ].filter(Boolean).length;

    const subtitle = 'Registo completo das apostas, com resultados e lucro.';

    if (loading || error) {
        return (
            <div className="page-bg-overlay">
                <main className="page-container bets-page-container">
                    <PageHeader eyebrow="Apostas" title="Bets" subtitle={subtitle} />
                    {loading ? (
                        <SkeletonBetList />
                    ) : (
                        <div className="error-state">
                            <p>Erro ao carregar apostas: {error}</p>
                        </div>
                    )}
                </main>
            </div>
        );
    }

    return (
        <div className="page-bg-overlay">
            <main className="page-container bets-page-container">
                <PageHeader
                    eyebrow="Apostas"
                    title="Bets"
                    subtitle={subtitle}
                    actions={
                        <>
                            {/* Seletor de Mês */}
                            <div className="month-selector-wrapper" ref={monthDropdownRef}>
                                <button 
                                    className={`btn month-selector-btn ${selectedMonth ? 'active' : ''}`}
                                    onClick={() => setShowMonthDropdown(!showMonthDropdown)}
                                >
                                    <FaCalendarAlt />
                                    <span>{currentMonthLabel}</span>
                                </button>
                                {showMonthDropdown && (
                                    <div className="month-dropdown">
                                        <button 
                                            className={`month-dropdown-item ${!selectedMonth ? 'active' : ''}`}
                                            onClick={handleViewAll}
                                        >
                                            Todos os Meses
                                        </button>
                                        <div className="month-dropdown-divider"></div>
                                        {availableMonths.map((item, index) => (
                                            <button 
                                                key={index}
                                                className={`month-dropdown-item ${selectedMonth === item.month && selectedYear === item.year ? 'active' : ''}`}
                                                onClick={() => handleMonthSelect(item.month, item.year)}
                                            >
                                                <span>{monthNames[item.month]} {item.year}</span>
                                                <span className="month-count">{item.count}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <button 
                                className={`btn filter-toggle-btn ${showFilters ? 'active' : ''} ${hasActiveFilters ? 'has-filters' : ''}`}
                                onClick={() => setShowFilters(!showFilters)}
                            >
                                <FaFilter />
                                <span>Filtros</span>
                                {activeFiltersCount > 0 && <span className="filter-count">{activeFiltersCount}</span>}
                            </button>
                        </>
                    }
                >
                    {currentStreak >= 2 && (
                        <div className={`streak-showcase streak-level-${Math.min(currentStreak, 5)}`}>
                            <div className="streak-fire-container">
                                <FaFire className="streak-fire-icon" />
                            </div>
                            <div className="streak-showcase-number">{currentStreak}</div>
                            <div className="streak-showcase-info">
                                <span className="streak-showcase-title">Hot Streak</span>
                                <span className="streak-showcase-subtitle">Greens seguidos</span>
                            </div>
                        </div>
                    )}
                    {lossStreak >= 2 && (
                        <div className={`loss-streak-showcase loss-streak-level-${Math.min(lossStreak, 5)}`}>
                            <div className="streak-loss-container">
                                <FaTimes className="streak-loss-icon" />
                            </div>
                            <div className="loss-streak-showcase-number">{lossStreak}</div>
                            <div className="loss-streak-showcase-info">
                                <span className="loss-streak-showcase-title">Loss Streak</span>
                                <span className="loss-streak-showcase-subtitle">Reds seguidos</span>
                            </div>
                        </div>
                    )}
                </PageHeader>

                {showFilters && (
                    <div className="filters-container">
                        <div className="filters-grid">
                            <div className="filter-group">
                                <label className="filter-label">Desporto</label>
                                <select 
                                    className="filter-select"
                                    value={filters.sport}
                                    onChange={(e) => handleFilterChange('sport', e.target.value)}
                                >
                                    <option value="all">Todos</option>
                                    {sports.map(sport => (
                                        <option key={sport} value={sport}>
                                            {sportNames[sport] || sport}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">Resultado</label>
                                <select 
                                    className="filter-select"
                                    value={filters.result}
                                    onChange={(e) => handleFilterChange('result', e.target.value)}
                                >
                                    <option value="all">Todos</option>
                                    {results.map(result => (
                                        <option key={result} value={result}>
                                            {resultNames[result]}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="filter-group">
                                <label className="filter-label">Tipo</label>
                                <select
                                    className="filter-select"
                                    value={filters.betType}
                                    onChange={(e) => handleFilterChange('betType', e.target.value)}
                                >
                                    <option value="all">Todas</option>
                                    <option value="simple">Simples</option>
                                    <option value="multiple">Múltipla</option>
                                </select>
                            </div>
                        </div>
                        {hasActiveFilters && (
                            <button className="btn btn-ghost clear-filters-btn" onClick={clearFilters}>
                                Limpar Filtros
                            </button>
                        )}
                    </div>
                )}

                <BetsSummary stats={stats} bets={filteredBets} />

                <BetsBreakdown
                    bets={filteredBets}
                    sportNames={sportNames}
                    sportIcons={sportIcons}
                    sportColors={sportColors}
                />

                {/* Legenda dos Desportos */}
                <div className="legend-toggle-wrapper">
                    <button 
                        className={`legend-toggle-btn ${showLegend ? 'active' : ''}`}
                        onClick={() => setShowLegend(!showLegend)}
                    >
                        <span>Legenda</span>
                        <FaChevronRight className={`legend-toggle-icon ${showLegend ? 'rotated' : ''}`} />
                    </button>
                </div>
                {showLegend && (
                    <div className="sports-legend">
                        <div className="legend-items">
                            {Object.entries(sportIcons).map(([sport, Icon]) => (
                                <div key={sport} className="legend-item">
                                    <Icon style={{ color: sportColors[sport] || '#fff' }} />
                                    <span>{sportNames[sport] || sport}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                <div className={`bets-list ${isPageChanging ? 'page-changing' : ''} ${loading ? 'loading' : ''}`} key={`list-${selectedMonth}-${selectedYear}`} ref={betsListRef}>
                    {filteredBets.length === 0 ? (
                        <div className="no-bets">
                            <p>Nenhuma aposta encontrada.</p>
                        </div>
                    ) : (
                        currentBets.map(bet => {
                            const potentialWin = ((bet.amount || 0) * bet.totalOdd).toFixed(2);
                            const betType = bet.games.length > 1 ? `Múltipla (${bet.games.length})` : 'Simples';
                            
                            return (
                                <div key={bet.id} className={`bet-card ${bet.result}`}>
                                    <div className="bet-card-header">
                                        <div className="bet-header-left">
                                            <div className="bet-type-badge">
                                                {betType}
                                            </div>
                                            {bet.date && <span className="bet-date">{formatDate(bet.date)}</span>}
                                        </div>
                                        <div className={`bet-result-badge ${bet.result}`}>
                                            {bet.result === 'won' ? (
                                                <>
                                                    <FaCheck className="result-icon" />
                                                    <span>Ganho</span>
                                                </>
                                            ) : bet.result === 'lost' ? (
                                                <>
                                                    <FaTimes className="result-icon" />
                                                    <span>Perdido</span>
                                                </>
                                            ) : bet.result === 'void' ? (
                                                <span>Anulada</span>
                                            ) : (
                                                <span>Pendente</span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="bet-games-list">
                                        {bet.games.map((game, index) => {
                                            const GameSportIcon = sportIcons[game.sport];
                                            const gameResultClass = game.gameResult ? `game-${game.gameResult}` : '';
                                            const isBetBuilder = game.extraPicks && game.extraPicks.length > 0;
                                            const selections = isBetBuilder
                                                ? [{ pick: game.pick, odd: game.gameOdd }, ...game.extraPicks]
                                                : [];
                                            const displayOdd = isBetBuilder ? game.combinedOdd : game.gameOdd;
                                            return (
                                                <div key={game.id} className={`bet-game-item ${gameResultClass}`}>
                                                    <div className="bets-game-sport-icon" style={{ color: sportColors[game.sport] }}>
                                                        {GameSportIcon && <GameSportIcon />}
                                                    </div>
                                                    <div className="bets-game-info">
                                                        {isBetBuilder ? (
                                                            <>
                                                                <GameTeams game={game} live={liveScores[game.id] || game.live_result} />
                                                                <GameWhen game={game} live={liveScores[game.id] || game.live_result} />
                                                                <GameIncidents game={game} live={liveScores[game.id] || game.live_result} />
                                                                <div className="bb-sub-timeline">
                                                                    {selections.map((sel, selIndex) => (
                                                                        <div className="bb-sub-pick" key={selIndex}>
                                                                            <span className="bb-sub-dot"></span>
                                                                            <span className="bb-sub-label">{sel.pick || `Seleção ${selIndex + 1}`}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {(game.pick || game.gameResult) && (
                                                                    <div className="bets-game-pick-row">
                                                                        {game.pick && (
                                                                            <span className={`bets-game-pick-value ${gameResultClass}`}>
                                                                                {game.pick}
                                                                            </span>
                                                                        )}
                                                                        {game.gameResult === 'void' && <span className="void-badge">Anulado</span>}
                                                                        {game.gameResult && game.gameResult !== 'pending' && game.gameResult !== 'void' && (
                                                                            <div className={`game-result-indicator ${game.gameResult}`}>
                                                                                {game.gameResult === 'won' ? <FaCheck /> : <FaTimes />}
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                )}
                                                                <GameTeams game={game} live={liveScores[game.id] || game.live_result} />
                                                                <GameWhen game={game} live={liveScores[game.id] || game.live_result} />
                                                                <GameIncidents game={game} live={liveScores[game.id] || game.live_result} />
                                                            </>
                                                        )}
                                                    </div>
                                                    <div className={`bets-game-odd-display ${gameResultClass}`}>
                                                        <span className="bets-odd-label">Odd</span>
                                                        <span className="bets-odd-value">{isBetBuilder ? Number(displayOdd).toFixed(2) : game.gameOdd}</span>
                                                    </div>
                                                    {index < bet.games.length - 1 && <div className="bets-game-divider"></div>}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    <div className="bet-card-footer">
                                        <div className="bet-stat">
                                            <span className="bet-stat-label">Odd Total</span>
                                            <span className="bet-stat-value">{bet.totalOdd.toFixed(2)}</span>
                                        </div>
                                        <div className="bet-stat">
                                            <span className="bet-stat-label">Valor Apostado</span>
                                            <span className="bet-stat-value">{(bet.amount || 0).toFixed(2)}€</span>
                                        </div>
                                        <div className={`bet-stat ${bet.result === 'won' ? 'highlight-won' : bet.result === 'lost' ? 'highlight-lost' : bet.result === 'void' ? 'highlight-void' : ''}`}>
                                            <span className="bet-stat-label">
                                                {bet.result === 'won' ? 'Ganho' : bet.result === 'void' ? 'Anulada' : 'Potencial'}
                                            </span>
                                            <span className="bet-stat-value">{bet.result === 'void' ? '-' : `${potentialWin}€`}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
                {filteredBets.length > 0 && (
                    <p className="bets-list-count">
                        A mostrar <span className="num">{startIndex + 1}–{Math.min(endIndex, filteredBets.length)}</span> de <span className="num">{filteredBets.length}</span> apostas
                    </p>
                )}
                {totalPages > 1 && (
                    <div className="pagination">
                        <button 
                            className="pagination-btn" 
                            onClick={() => goToPage(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            <FaChevronLeft />
                        </button>
                        
                        <div className="pagination-info">
                            <span className="pagination-current">{currentPage}</span>
                            <span className="pagination-separator">/</span>
                            <span className="pagination-total">{totalPages}</span>
                        </div>
                        
                        <button 
                            className="pagination-btn" 
                            onClick={() => goToPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            <FaChevronRight />
                        </button>
                    </div>
                )}
            </main>
        </div>
    );
}

export default BetsPage;
